import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { OttProxyHealthBar } from '@/components/ott/OttProxyHealthBar';
import {
  Tv, Plus, Pencil, Trash2, Tag, IndianRupee,
  Package, Users, Loader2, X, Wallet, Search, UserCircle,
  RefreshCw, AlertTriangle, CheckCircle2, TicketIcon, ChevronDown, ChevronUp, Eye,
  Cloud, Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============== Pack Form ==============
function PackForm({ pack, onSave, onCancel }) {
  const [form, setForm] = useState({
    provider: pack?.provider || '',
    pack_name: pack?.pack_name || '',
    description: pack?.description || '',
    base_price: pack?.base_price || '',
    validity_days: pack?.validity_days || 30,
    plan_code: pack?.plan_code || '',
    channels: pack?.channels?.join(', ') || '',
    features: pack?.features?.join(', ') || '',
    is_active: pack?.is_active !== false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.provider || !form.pack_name || !form.base_price) {
      toast.error('Provider, pack name, and price are required');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        base_price: parseFloat(form.base_price),
        validity_days: parseInt(form.validity_days),
        plan_code: form.plan_code || '',
        channels: form.channels ? form.channels.split(',').map(c => c.trim()).filter(Boolean) : [],
        features: form.features ? form.features.split(',').map(f => f.trim()).filter(Boolean) : []
      });
    } finally { setSaving(false); }
  };

  const providers = [
    'Watcho', 'PlayBox TV', 'Disney+ Hotstar', 'SonyLIV', 'ZEE5',
    'Amazon Prime', 'Netflix', 'JioCinema', 'Sun NXT', 'Aha',
    'FanCode', 'Hungama', 'Lionsgate Play', 'Hoichoi', 'ShemarooMe',
    'EpicON', 'Chaupal', 'Other'
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Provider</Label>
          <Select value={form.provider} onValueChange={v => setForm(p => ({ ...p, provider: v }))}>
            <SelectTrigger data-testid="pack-provider-select"><SelectValue placeholder="Select provider" /></SelectTrigger>
            <SelectContent>
              {providers.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Pack Name</Label>
          <Input data-testid="pack-name-input" value={form.pack_name} onChange={e => setForm(p => ({ ...p, pack_name: e.target.value }))} placeholder="e.g., Gold Pack" />
        </div>
      </div>
      <div>
        <Label>Plan Code <span className="text-xs text-slate-400 font-normal">(Provider's pack/plan ID for API activation)</span></Label>
        <Input data-testid="pack-plan-code-input" value={form.plan_code} onChange={e => setForm(p => ({ ...p, plan_code: e.target.value }))} placeholder="e.g., PB-GOLD-30, WATCHO-299" />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea data-testid="pack-description-input" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Pack description" rows={2} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Base Price (Rs.)</Label>
          <Input data-testid="pack-price-input" type="number" step="0.01" min="1" value={form.base_price} onChange={e => setForm(p => ({ ...p, base_price: e.target.value }))} />
        </div>
        <div>
          <Label>Validity (days)</Label>
          <Input data-testid="pack-validity-input" type="number" min="1" max="365" value={form.validity_days} onChange={e => setForm(p => ({ ...p, validity_days: e.target.value }))} />
        </div>
      </div>
      <div>
        <Label>Channels (comma separated)</Label>
        <Input value={form.channels} onChange={e => setForm(p => ({ ...p, channels: e.target.value }))} placeholder="Movies, Sports, News" />
      </div>
      <div>
        <Label>Features (comma separated)</Label>
        <Input value={form.features} onChange={e => setForm(p => ({ ...p, features: e.target.value }))} placeholder="HD Quality, Multi-device" />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="pack-save-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {pack ? 'Update Pack' : 'Create Pack'}
        </Button>
      </DialogFooter>
    </form>
  );
}

// ============== Manual Top-Up Dialog ==============
function ManualTopUpDialog({ token, onClose, onSuccess }) {
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('credit');  // 'credit' | 'debit'
  const [form, setForm] = useState({ amount: '', payment_mode: 'bank_transfer', transaction_ref: '', notes: '', description: '' });
  const [debitForm, setDebitForm] = useState({ amount: '', reason: '', notes: '', allow_negative: false });
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  const searchCustomers = async (q) => {
    if (!q || q.length < 2) return;
    setSearching(true);
    try {
      const res = await fetch(`${API}/api/wallet/search-customers?q=${encodeURIComponent(q)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data.customers || []);
      }
    } catch (err) { console.error('Customer search failed:', err); }
    setSearching(false);
  };

  useEffect(() => {
    const t = setTimeout(() => searchCustomers(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const handleSubmit = async () => {
    if (mode === 'credit') {
      if (!selected || !form.amount || parseFloat(form.amount) <= 0) {
        toast.error('Select a partner and enter amount');
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/wallet/manual-credit`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: selected.customer_id,
            amount: parseFloat(form.amount),
            payment_mode: form.payment_mode,
            transaction_ref: form.transaction_ref,
            notes: form.notes,
            description: form.description || `Manual Top-up (${form.payment_mode})`
          })
        });
        const data = await res.json();
        if (data.success) {
          toast.success(`Rs.${form.amount} credited to ${selected.company_name}. New balance: Rs.${data.new_balance.toFixed(2)}`);
          onSuccess();
          onClose();
        } else {
          toast.error(data.detail || 'Failed to credit');
        }
      } catch { toast.error('Network error'); }
      setLoading(false);
    } else {
      // Debit mode
      if (!selected || !debitForm.amount || parseFloat(debitForm.amount) <= 0) {
        toast.error('Select a partner and enter amount');
        return;
      }
      if (!debitForm.reason.trim()) {
        toast.error('Reason is required for a debit');
        return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/wallet/manual-debit`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: selected.customer_id,
            amount: parseFloat(debitForm.amount),
            reason: debitForm.reason,
            notes: debitForm.notes,
            allow_negative: debitForm.allow_negative,
          })
        });
        const data = await res.json();
        if (res.ok && data.success) {
          toast.success(`Rs.${debitForm.amount} debited from ${selected.company_name}. New balance: Rs.${data.new_balance.toFixed(2)}`);
          onSuccess();
          onClose();
        } else {
          toast.error(data.detail || 'Failed to debit');
        }
      } catch { toast.error('Network error'); }
      setLoading(false);
    }
  };

  const isCredit = mode === 'credit';
  const submitDisabled = loading || !selected || (isCredit ? !form.amount : (!debitForm.amount || !debitForm.reason.trim()));

  return (
    <DialogContent className="max-w-md" data-testid="ott-wallet-adjust-dialog">
      <DialogHeader>
        <DialogTitle>Adjust Wallet Balance</DialogTitle>
        <DialogDescription>
          {isCredit ? 'Credit partner wallet with transaction details' : 'Debit (deduct) from partner wallet — requires a reason'}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        {/* Partner Search */}
        {!selected ? (
          <div className="space-y-2">
            <Label>Search Partner</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input
                className="pl-9"
                placeholder="Search by name, phone, ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="topup-search-input"
                autoFocus
              />
            </div>
            {searching && <p className="text-xs text-slate-400">Searching...</p>}
            {customers.length > 0 && (
              <div className="border rounded-md max-h-40 overflow-y-auto divide-y">
                {customers.map(c => (
                  <button
                    key={c.customer_id}
                    className="w-full text-left p-2 hover:bg-slate-50 flex justify-between items-center text-sm"
                    onClick={() => setSelected(c)}
                    data-testid={`select-customer-${c.customer_id}`}
                  >
                    <div>
                      <p className="font-medium">{c.company_name}</p>
                      <p className="text-xs text-slate-500">{c.contact_phone}</p>
                    </div>
                    <span className="text-xs font-mono text-slate-500">Bal: Rs.{c.wallet_balance}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-slate-50 p-3 rounded-md flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">{selected.company_name}</p>
              <p className="text-xs text-slate-500">{selected.contact_phone} | Bal: Rs.{selected.wallet_balance}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setSelected(null)}><X className="h-4 w-4" /></Button>
          </div>
        )}

        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="credit" data-testid="tab-credit">Credit (Add)</TabsTrigger>
            <TabsTrigger value="debit" className="data-[state=active]:bg-red-50 data-[state=active]:text-red-700" data-testid="tab-debit">Debit (Deduct)</TabsTrigger>
          </TabsList>
          <TabsContent value="credit" className="space-y-4 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (Rs.)</Label>
                <Input type="number" min="1" step="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} data-testid="topup-amount-input" />
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={v => setForm(p => ({ ...p, payment_mode: v }))}>
                  <SelectTrigger data-testid="topup-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="neft">NEFT/RTGS</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Transaction Ref / UTR / Cheque No</Label>
              <Input value={form.transaction_ref} onChange={e => setForm(p => ({ ...p, transaction_ref: e.target.value }))} placeholder="e.g., UTR123456789" data-testid="topup-ref-input" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any additional notes..." rows={2} data-testid="topup-notes-input" />
            </div>
          </TabsContent>
          <TabsContent value="debit" className="space-y-4 pt-3">
            <div>
              <Label>Amount to Debit (Rs.) *</Label>
              <Input type="number" min="1" step="0.01" value={debitForm.amount}
                onChange={e => setDebitForm(p => ({ ...p, amount: e.target.value }))}
                data-testid="debit-amount-input" />
              {selected && debitForm.amount && parseFloat(debitForm.amount) > parseFloat(selected.wallet_balance || 0) && !debitForm.allow_negative && (
                <p className="text-xs text-red-600 mt-1">⚠ Amount exceeds current balance (Rs.{selected.wallet_balance}). Enable "Allow negative balance" below to proceed.</p>
              )}
            </div>
            <div>
              <Label>Reason *</Label>
              <Input value={debitForm.reason}
                onChange={e => setDebitForm(p => ({ ...p, reason: e.target.value }))}
                placeholder="e.g., Refund processed, Manual correction, Service deactivated"
                data-testid="debit-reason-input" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={debitForm.notes}
                onChange={e => setDebitForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Additional context (optional)" rows={2}
                data-testid="debit-notes-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={debitForm.allow_negative}
                onChange={e => setDebitForm(p => ({ ...p, allow_negative: e.target.checked }))}
                data-testid="debit-allow-negative" />
              Allow negative wallet balance
            </label>
          </TabsContent>
        </Tabs>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={submitDisabled}
          className={isCredit ? '' : 'bg-red-600 hover:bg-red-700'}
          data-testid={isCredit ? 'topup-submit-btn' : 'debit-submit-btn'}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <IndianRupee className="h-4 w-4 mr-2" />}
          {isCredit ? `Credit Rs.${form.amount || '0'}` : `Debit Rs.${debitForm.amount || '0'}`}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

// ============== Partner Pricing Dialog ==============
function PartnerPricingDialog({ pack, token, onClose, onUpdated }) {
  const [tab, setTab] = useState('type'); // type or partner
  const [typeForm, setTypeForm] = useState({ customer_type: '', price: '', discount_percent: '0' });
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerResults, setPartnerResults] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerPrice, setPartnerPrice] = useState('');
  const [saving, setSaving] = useState(false);

  const searchPartners = async (q) => {
    if (!q || q.length < 2) return;
    const res = await fetch(`${API}/api/wallet/search-customers?q=${encodeURIComponent(q)}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      setPartnerResults(data.customers || []);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => searchPartners(partnerSearch), 300);
    return () => clearTimeout(t);
  }, [partnerSearch]);

  const handleSaveTypePricing = async () => {
    if (!typeForm.customer_type || !typeForm.price) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ott/packs/${pack.pack_id}/pricing`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_type: typeForm.customer_type, price: parseFloat(typeForm.price), discount_percent: parseFloat(typeForm.discount_percent || 0) })
      });
      if (res.ok) { toast.success('Type pricing updated'); onUpdated(); setTypeForm({ customer_type: '', price: '', discount_percent: '0' }); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const handleSavePartnerPricing = async () => {
    if (!selectedPartner || !partnerPrice) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ott/packs/${pack.pack_id}/partner-pricing`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: selectedPartner.customer_id, price: parseFloat(partnerPrice) })
      });
      if (res.ok) { toast.success(`Price set for ${selectedPartner.company_name}`); onUpdated(); setSelectedPartner(null); setPartnerPrice(''); setPartnerSearch(''); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const handleRemoveType = async (type) => {
    await fetch(`${API}/api/ott/packs/${pack.pack_id}/pricing/${type}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    toast.success('Removed'); onUpdated();
  };

  const handleRemovePartner = async (cid) => {
    await fetch(`${API}/api/ott/packs/${pack.pack_id}/partner-pricing/${cid}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    toast.success('Removed'); onUpdated();
  };

  const existingType = pack.custom_pricing || {};
  const existingPartner = pack.partner_pricing || {};

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Pricing - {pack.pack_name}</DialogTitle>
        <DialogDescription>Base price: Rs.{pack.base_price} | Set pricing by type or per individual partner</DialogDescription>
      </DialogHeader>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-4">
        <button onClick={() => setTab('type')} className={`flex-1 py-1.5 rounded-md text-sm font-medium ${tab === 'type' ? 'bg-white shadow-sm' : 'text-slate-500'}`}>By Type</button>
        <button onClick={() => setTab('partner')} className={`flex-1 py-1.5 rounded-md text-sm font-medium ${tab === 'partner' ? 'bg-white shadow-sm' : 'text-slate-500'}`} data-testid="pricing-partner-tab">By Partner</button>
      </div>

      {tab === 'type' && (
        <div className="space-y-3">
          {Object.keys(existingType).length > 0 && (
            <div className="space-y-1">
              {Object.entries(existingType).map(([type, p]) => (
                <div key={type} className="flex items-center justify-between bg-slate-50 rounded-md p-2 text-sm">
                  <span className="capitalize font-medium">{type}</span>
                  <div className="flex items-center gap-2">
                    <span>Rs.{p.price}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleRemoveType(type)}><X className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Select value={typeForm.customer_type} onValueChange={v => setTypeForm(p => ({ ...p, customer_type: v }))}>
            <SelectTrigger><SelectValue placeholder="Customer Type" /></SelectTrigger>
            <SelectContent>
              {['retail', 'partner', 'mso', 'bulk', 'corporate'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder="Price" value={typeForm.price} onChange={e => setTypeForm(p => ({ ...p, price: e.target.value }))} />
            <Input type="number" placeholder="Discount %" value={typeForm.discount_percent} onChange={e => setTypeForm(p => ({ ...p, discount_percent: e.target.value }))} />
          </div>
          <Button onClick={handleSaveTypePricing} disabled={saving} className="w-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Set Type Pricing
          </Button>
        </div>
      )}

      {tab === 'partner' && (
        <div className="space-y-3">
          {Object.keys(existingPartner).length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-slate-500">Individual Partner Prices</p>
              {Object.entries(existingPartner).map(([cid, p]) => (
                <div key={cid} className="flex items-center justify-between bg-slate-50 rounded-md p-2 text-sm">
                  <div>
                    <span className="font-medium">{p.customer_name || cid.slice(0, 8)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Rs.{p.price}</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleRemovePartner(cid)}><X className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!selectedPartner ? (
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input className="pl-9" placeholder="Search partner..." value={partnerSearch} onChange={e => setPartnerSearch(e.target.value)} data-testid="pricing-partner-search" />
              </div>
              {partnerResults.length > 0 && (
                <div className="border rounded-md mt-1 max-h-32 overflow-y-auto divide-y">
                  {partnerResults.map(c => (
                    <button key={c.customer_id} className="w-full text-left p-2 hover:bg-slate-50 text-sm" onClick={() => { setSelectedPartner(c); setPartnerPrice(''); }}>
                      <p className="font-medium">{c.company_name}</p>
                      <p className="text-xs text-slate-500">{c.contact_phone}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-50 p-2 rounded-md flex items-center justify-between">
              <div className="text-sm">
                <p className="font-medium">{selectedPartner.company_name}</p>
                <p className="text-xs text-slate-500">{selectedPartner.contact_phone}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSelectedPartner(null)}><X className="h-4 w-4" /></Button>
            </div>
          )}
          {selectedPartner && (
            <>
              <Input type="number" placeholder="Price for this partner" value={partnerPrice} onChange={e => setPartnerPrice(e.target.value)} data-testid="partner-price-input" />
              <Button onClick={handleSavePartnerPricing} disabled={saving} className="w-full" data-testid="partner-pricing-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCircle className="h-4 w-4 mr-2" />}
                Set Price for {selectedPartner.company_name}
              </Button>
            </>
          )}
        </div>
      )}
    </DialogContent>
  );
}

// ============== Main Page ==============
export default function OTTManagement() {
  const { token } = useAuth();
  const [tab, setTab] = useState('packs');
  const [packs, setPacks] = useState([]);
  const [activations, setActivations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editPack, setEditPack] = useState(null);
  const [pricingPack, setPricingPack] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);
  const [expandedAct, setExpandedAct] = useState(null);
  const [detailAct, setDetailAct] = useState(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchPacks = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ott/packs?active_only=false`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setPacks(data.packs || []); }
    } catch { /* */ }
  }, [token]);

  const fetchActivations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ott/activations?limit=100`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setActivations(data.activations || []); }
    } catch { /* */ }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/ott/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setStats(await res.json());
    } catch { /* */ }
  }, [token]);

  // ---- Watcho catalog state ----
  const [watchoPackages, setWatchoPackages] = useState([]);
  const [watchoLastSync, setWatchoLastSync] = useState(null);
  const [watchoLoading, setWatchoLoading] = useState(false);
  const [watchoSyncing, setWatchoSyncing] = useState(false);
  const [watchoDurationFilter, setWatchoDurationFilter] = useState('all');

  // ---- Watcho wallet balance state ----
  const [watchoBalance, setWatchoBalance] = useState(null);
  const [watchoBalanceRefreshing, setWatchoBalanceRefreshing] = useState(false);

  const fetchWatchoBalance = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/watcho/wallet-balance`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setWatchoBalance(await res.json());
    } catch { /* */ }
  }, [token]);

  const refreshWatchoBalance = async () => {
    setWatchoBalanceRefreshing(true);
    try {
      const res = await fetch(`${API}/api/watcho/wallet-balance/refresh`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.ok) toast.success(`Watcho balance: Rs.${data.balance?.toLocaleString('en-IN')}`);
      else toast.error(data.error || 'Refresh failed');
      await fetchWatchoBalance();
    } catch { toast.error('Network error'); }
    setWatchoBalanceRefreshing(false);
  };

  const fetchWatchoPackages = useCallback(async () => {
    setWatchoLoading(true);
    try {
      const qs = watchoDurationFilter === 'all' ? '' : `?duration=${watchoDurationFilter}`;
      const res = await fetch(`${API}/api/watcho/packages${qs}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setWatchoPackages(data.packages || []);
        setWatchoLastSync(data.last_sync || null);
      }
    } catch { /* */ }
    setWatchoLoading(false);
  }, [token, watchoDurationFilter]);

  const runWatchoSync = async () => {
    setWatchoSyncing(true);
    try {
      const res = await fetch(`${API}/api/watcho/packages/sync-now`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.ok) toast.success(`Synced ${data.synced} Watcho plans`);
      else toast.error(data.error || 'Sync failed — IP may not be whitelisted on Watcho');
      await fetchWatchoPackages();
    } catch { toast.error('Network error'); }
    setWatchoSyncing(false);
  };

  const importOneWatcho = async (planId) => {
    try {
      const res = await fetch(`${API}/api/watcho/packages/${encodeURIComponent(planId)}/import-to-ott-pack`, {
        method: 'POST', headers, body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Plan ${planId} → ${data.action} in OTT Packs`);
        fetchPacks();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch { toast.error('Network error'); }
  };

  const importAllWatcho = async () => {
    if (!window.confirm('Import ALL active Watcho plans to OTT Packs? Existing imports will be updated.')) return;
    setWatchoSyncing(true);
    try {
      const res = await fetch(`${API}/api/watcho/packages/import-all`, {
        method: 'POST', headers, body: JSON.stringify({ overwrite_prices: true })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
        fetchPacks();
      } else toast.error('Import failed');
    } catch { toast.error('Network error'); }
    setWatchoSyncing(false);
  };

  useEffect(() => {
    if (tab === 'watcho') fetchWatchoPackages();
  }, [tab, fetchWatchoPackages]);

  useEffect(() => {
    fetchWatchoBalance();
    const t = setInterval(fetchWatchoBalance, 5 * 60 * 1000); // re-pull cached balance every 5 min
    return () => clearInterval(t);
  }, [fetchWatchoBalance]);

  useEffect(() => {
    Promise.all([fetchPacks(), fetchActivations(), fetchStats()]).finally(() => setLoading(false));
  }, [fetchPacks, fetchActivations, fetchStats]);

  const handleSavePack = async (formData) => {
    const url = editPack ? `${API}/api/ott/packs/${editPack.pack_id}` : `${API}/api/ott/packs`;
    const method = editPack ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, { method, headers, body: JSON.stringify(formData) });
      if (res.ok) { toast.success(editPack ? 'Pack updated' : 'Pack created'); setShowForm(false); setEditPack(null); fetchPacks(); fetchStats(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
  };

  const handleDeletePack = async (packId) => {
    if (!window.confirm('Deactivate this pack?')) return;
    const res = await fetch(`${API}/api/ott/packs/${packId}`, { method: 'DELETE', headers });
    if (res.ok) { toast.success('Pack deactivated'); fetchPacks(); fetchStats(); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="ott-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">OTT Subscriptions</h1>
          <p className="text-sm text-slate-500 mt-1">Manage OTT packages, pricing, wallets, and activations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTopUp(true)} data-testid="manual-topup-btn">
            <Wallet className="h-4 w-4 mr-2" /> Manual Top-Up
          </Button>
          <Button onClick={() => { setEditPack(null); setShowForm(true); }} data-testid="create-pack-btn">
            <Plus className="h-4 w-4 mr-2" /> New Pack
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Packs', value: stats.total_packs, icon: Package, color: 'text-slate-900' },
            { label: 'Active Subs', value: stats.active_subscriptions, icon: Users, color: 'text-emerald-600' },
            { label: 'Total Activations', value: stats.total_activations, icon: Tv, color: 'text-sky-600' },
            { label: 'Revenue', value: `Rs.${stats.total_revenue?.toLocaleString('en-IN') || 0}`, icon: IndianRupee, color: 'text-slate-900' }
          ].map(s => (
            <Card key={s.label}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  </div>
                  <s.icon className="h-8 w-8 text-slate-200" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Watcho Wallet Balance */}
      <Card data-testid="watcho-wallet-balance-card" className="border-sky-100 bg-gradient-to-r from-sky-50 to-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Cloud className="h-8 w-8 text-sky-600" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Watcho Wallet Balance</p>
                <p className="text-2xl font-bold text-sky-700" data-testid="watcho-balance-value">
                  {watchoBalance?.balance != null
                    ? `Rs.${Number(watchoBalance.balance).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                    : '—'}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {watchoBalance?.entity_name
                    ? `${watchoBalance.entity_name} · ${watchoBalance.entity_type || ''} ${watchoBalance.entity_id || ''}`.trim()
                    : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-right">
              <div className="text-xs text-slate-500">
                {watchoBalance?.last_sync_at ? (
                  <>Last synced<br />{new Date(watchoBalance.last_sync_at).toLocaleString('en-IN')}</>
                ) : 'Not yet synced'}
                {watchoBalance?.last_error && (
                  <div className="text-rose-600 mt-1">{watchoBalance.last_error}</div>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={refreshWatchoBalance} disabled={watchoBalanceRefreshing} data-testid="refresh-watcho-balance-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${watchoBalanceRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {[
          { id: 'packs', label: 'Packs', icon: Package },
          { id: 'activations', label: 'Activations', icon: Tv },
          { id: 'watcho', label: 'Watcho Catalog', icon: Cloud },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Packs Tab */}
      {tab === 'packs' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {packs.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <Package className="mx-auto h-12 w-12 text-slate-300 mb-3" />
                <p className="text-slate-500">No OTT packs configured yet</p>
                <Button className="mt-4" onClick={() => setShowForm(true)}><Plus className="h-4 w-4 mr-2" /> Create First Pack</Button>
              </CardContent>
            </Card>
          ) : packs.map(pack => (
            <Card key={pack.pack_id} className={`relative ${!pack.is_active ? 'opacity-60' : ''}`} data-testid={`pack-card-${pack.pack_id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="mb-2 text-xs">{pack.provider}</Badge>
                    <CardTitle className="text-base">{pack.pack_name}</CardTitle>
                    <CardDescription className="text-xs mt-1">{pack.description || 'No description'}</CardDescription>
                  </div>
                  {!pack.is_active && <Badge className="bg-slate-200 text-slate-600">Inactive</Badge>}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-slate-900">Rs.{pack.base_price}</span>
                  <span className="text-sm text-slate-500">/ {pack.validity_days} days</span>
                </div>
                {pack.plan_code && <p className="text-xs text-slate-500 font-mono bg-slate-50 px-2 py-1 rounded w-fit">Plan Code: {pack.plan_code}</p>}
                {pack.channels?.length > 0 && (
                  <div className="flex flex-wrap gap-1">{pack.channels.map(c => <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>)}</div>
                )}
                {Object.keys(pack.custom_pricing || {}).length > 0 && (
                  <div className="text-xs text-slate-500">
                    Type pricing: {Object.entries(pack.custom_pricing).map(([t, p]) => <span key={t} className="capitalize">{t}: Rs.{p.price} </span>)}
                  </div>
                )}
                {Object.keys(pack.partner_pricing || {}).length > 0 && (
                  <div className="text-xs text-blue-600">
                    Partner pricing: {Object.values(pack.partner_pricing).map((p, i) => <span key={i}>{p.customer_name}: Rs.{p.price} </span>)}
                  </div>
                )}
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline" onClick={() => { setEditPack(pack); setShowForm(true); }}>
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setPricingPack(pack)}>
                    <Tag className="h-3 w-3 mr-1" /> Pricing
                  </Button>
                  {pack.is_active && (
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeletePack(pack.pack_id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Activations Tab */}
      {tab === 'activations' && (
        <>
        <OttProxyHealthBar token={token} onRetried={() => { fetchActivations(); fetchStats(); }} />
        <Card>
          <CardContent className="p-0">
            {activations.length === 0 ? (
              <div className="py-12 text-center"><Tv className="mx-auto h-12 w-12 text-slate-300 mb-3" /><p className="text-slate-500">No activations yet</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="activations-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="p-3 text-left w-8"></th>
                      <th className="p-3 text-left">Customer</th>
                      <th className="p-3 text-left">Subscriber</th>
                      <th className="p-3 text-left">Pack</th>
                      <th className="p-3 text-left">Mobile</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3 text-left">Period</th>
                      <th className="p-3 text-left">Provider Status</th>
                      <th className="p-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {activations.map(act => (
                      <React.Fragment key={act.activation_id}>
                        <tr className="hover:bg-slate-50">
                          <td className="p-3">
                            {(act.provider_otts?.length > 0 || act.provider_status === 'activated') && (
                              <button
                                onClick={() => setExpandedAct(expandedAct === act.activation_id ? null : act.activation_id)}
                                className="p-0.5 hover:bg-slate-200 rounded"
                                data-testid={`expand-${act.activation_id}`}
                              >
                                {expandedAct === act.activation_id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                              </button>
                            )}
                          </td>
                          <td className="p-3 font-medium">{act.customer_name || act.customer_id?.slice(0, 8)}</td>
                          <td className="p-3 text-xs">{act.subscriber_name || '-'}</td>
                          <td className="p-3">
                            <div>{act.provider_pack_name || act.pack_name}</div>
                            <Badge variant="outline" className="text-xs">{act.provider}</Badge>
                          </td>
                          <td className="p-3 font-mono text-xs">{act.mobile_no}</td>
                          <td className="p-3 text-right font-medium">Rs.{act.amount_charged}</td>
                          <td className="p-3 text-xs">{act.start_date} to {act.end_date}</td>
                          <td className="p-3">
                            {act.provider_status === 'activated' ? (
                              <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1 inline" />Activated</Badge>
                            ) : act.provider_status === 'failed' ? (
                              <div>
                                <Badge className="bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1 inline" />Failed</Badge>
                                {act.provider_error && <p className="text-xs text-red-500 mt-1 max-w-48 truncate" title={act.provider_error}>{act.provider_error}</p>}
                              </div>
                            ) : act.provider_status === 'pending' ? (
                              <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-600">{act.status}</Badge>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              {(act.provider_status === 'failed' || act.provider_status === 'pending') && (
                                <Button size="sm" variant="outline" className="h-7 text-xs"
                                  onClick={async () => {
                                    const res = await fetch(`${API}/api/ott/activations/${act.activation_id}/retry`, {
                                      method: 'POST', headers
                                    });
                                    const data = await res.json();
                                    if (data.success) { toast.success(data.message); fetchActivations(); }
                                    else toast.error(data.message || 'Retry failed');
                                  }}
                                  data-testid={`retry-${act.activation_id}`}
                                >
                                  <RefreshCw className="h-3 w-3 mr-1" /> Retry
                                </Button>
                              )}
                              {act.provider_status === 'failed' && !act.ticket_raised && (
                                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={async () => {
                                    const res = await fetch(`${API}/api/ott/activations/${act.activation_id}/raise-ticket`, {
                                      method: 'POST', headers, body: JSON.stringify({})
                                    });
                                    const data = await res.json();
                                    if (data.success) { toast.success('Support ticket raised'); fetchActivations(); }
                                    else toast.error('Failed to raise ticket');
                                  }}
                                  data-testid={`ticket-${act.activation_id}`}
                                >
                                  <TicketIcon className="h-3 w-3 mr-1" /> Raise Ticket
                                </Button>
                              )}
                              {act.ticket_raised && (
                                <Badge variant="outline" className="text-xs text-blue-600">Ticket Raised</Badge>
                              )}
                              <Button size="sm" variant="ghost" className="h-7 text-xs"
                                onClick={() => setDetailAct(act)}
                                data-testid={`detail-${act.activation_id}`}
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded OTT details row */}
                        {expandedAct === act.activation_id && act.provider_otts?.length > 0 && (
                          <tr>
                            <td colSpan={9} className="bg-slate-50 px-6 py-3">
                              <div className="flex flex-wrap gap-2">
                                <span className="text-xs font-medium text-slate-500 mr-2">OTT Services ({act.provider_otts.length}):</span>
                                {act.provider_otts.map((ott, i) => (
                                  <Badge key={ott.ottCode || i} variant="secondary" className={`text-xs ${ott.statusCode === 2 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                    {ott.ottName} {ott.statusCode === 2 ? '' : '(pending)'}
                                  </Badge>
                                ))}
                              </div>
                              {act.provider_expiry && (
                                <p className="text-xs text-slate-400 mt-2">Provider Expiry: {new Date(act.provider_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                              )}
                              {act.provider_ref_id && (
                                <p className="text-xs text-slate-400 font-mono">Ref: {act.provider_ref_id}</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        </>
      )}

      {/* Watcho Catalog Tab */}
      {tab === 'watcho' && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Cloud className="h-5 w-5 text-sky-600" /> Watcho Plan Catalog
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Auto-synced from Watcho production API every hour. Import any plan into your OTT Packs to sell it.
                </p>
                {watchoLastSync && (
                  <p className="text-xs text-slate-400 mt-1">
                    Last sync: {watchoLastSync.finished_at ? new Date(watchoLastSync.finished_at).toLocaleString() : '—'}
                    {' · '}
                    {watchoLastSync.ok ? (
                      <span className="text-emerald-600">{watchoLastSync.synced} plans synced</span>
                    ) : (
                      <span className="text-red-600">Last error: {watchoLastSync.error || 'unknown'}</span>
                    )}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <select
                  value={watchoDurationFilter}
                  onChange={e => setWatchoDurationFilter(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="watcho-duration-filter"
                >
                  <option value="all">All durations</option>
                  <option value="1">1 month</option>
                  <option value="3">3 months</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                </select>
                <Button variant="outline" onClick={runWatchoSync} disabled={watchoSyncing} data-testid="watcho-sync-btn">
                  {watchoSyncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Sync Now
                </Button>
                <Button onClick={importAllWatcho} disabled={watchoSyncing || watchoPackages.length === 0}
                  className="bg-sky-600 hover:bg-sky-700"
                  data-testid="watcho-import-all-btn">
                  <Download className="h-4 w-4 mr-1" /> Import All to OTT Packs
                </Button>
              </div>
            </div>

            {watchoLoading ? (
              <div className="py-12 text-center"><Loader2 className="mx-auto h-8 w-8 text-slate-300 animate-spin" /></div>
            ) : watchoPackages.length === 0 ? (
              <div className="py-12 text-center">
                <Cloud className="mx-auto h-12 w-12 text-slate-300 mb-3" />
                <p className="text-slate-500">No Watcho plans in cache yet.</p>
                <p className="text-xs text-slate-400 mt-1">Click "Sync Now" — requires the Watcho whitelist to include this environment's egress IP.</p>
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm" data-testid="watcho-catalog-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="p-3 text-left">Plan ID</th>
                      <th className="p-3 text-left">Name</th>
                      <th className="p-3 text-left">Duration</th>
                      <th className="p-3 text-right">Price</th>
                      <th className="p-3 text-left">Description</th>
                      <th className="p-3 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {watchoPackages.map(p => (
                      <tr key={p.plan_id} className="hover:bg-slate-50" data-testid={`watcho-plan-${p.plan_id}`}>
                        <td className="p-3 font-mono text-xs">{p.plan_id}</td>
                        <td className="p-3 font-medium">{p.name}</td>
                        <td className="p-3 text-xs">
                          {p.duration_months ? `${p.duration_months}M` : (p.duration_days ? `${p.duration_days}d` : '—')}
                        </td>
                        <td className="p-3 text-right font-medium">Rs.{p.price ?? 0}</td>
                        <td className="p-3 text-xs text-slate-500 max-w-xs truncate" title={p.description}>{p.description || '—'}</td>
                        <td className="p-3">
                          <Button size="sm" variant="outline" onClick={() => importOneWatcho(p.plan_id)} data-testid={`watcho-import-${p.plan_id}`}>
                            <Download className="h-3 w-3 mr-1" /> Import
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <Dialog open={!!detailAct} onOpenChange={v => { if (!v) setDetailAct(null); }}>
        {detailAct && (
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Activation Details</DialogTitle>
              <DialogDescription>{detailAct.provider_pack_name || detailAct.pack_name} - {detailAct.mobile_no}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-500">Customer</span><p className="font-medium">{detailAct.customer_name}</p></div>
                <div><span className="text-slate-500">Subscriber</span><p className="font-medium">{detailAct.subscriber_name || '-'}</p></div>
                <div><span className="text-slate-500">Mobile</span><p className="font-mono">{detailAct.mobile_no}</p></div>
                <div><span className="text-slate-500">Amount</span><p className="font-medium">Rs.{detailAct.amount_charged}</p></div>
                <div><span className="text-slate-500">Period</span><p>{detailAct.start_date} to {detailAct.end_date}</p></div>
                <div><span className="text-slate-500">Status</span><p>{detailAct.provider_status || detailAct.status}</p></div>
                {detailAct.plan_code && <div><span className="text-slate-500">Plan Code</span><p className="font-mono">{detailAct.plan_code}</p></div>}
                {detailAct.provider_ref_id && <div><span className="text-slate-500">Provider Ref</span><p className="font-mono text-xs">{detailAct.provider_ref_id}</p></div>}
                {detailAct.provider_expiry && <div><span className="text-slate-500">Provider Expiry</span><p>{new Date(detailAct.provider_expiry).toLocaleDateString('en-IN')}</p></div>}
                {detailAct.retry_count > 0 && <div><span className="text-slate-500">Retries</span><p>{detailAct.retry_count}</p></div>}
              </div>
              {detailAct.provider_error && (
                <div className="bg-red-50 rounded-md p-3">
                  <span className="text-xs font-medium text-red-600">Error</span>
                  <p className="text-red-700 text-xs mt-1">{detailAct.provider_error}</p>
                </div>
              )}
              {detailAct.provider_otts?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Included OTT Services ({detailAct.provider_otts.length})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {detailAct.provider_otts.map((ott, i) => (
                      <Badge key={ott.ottCode || i} variant="secondary" className={`text-xs ${ott.statusCode === 2 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {ott.ottName}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* Dialogs */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) { setShowForm(false); setEditPack(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editPack ? 'Edit OTT Pack' : 'Create OTT Pack'}</DialogTitle>
            <DialogDescription>Configure OTT subscription package</DialogDescription>
          </DialogHeader>
          <PackForm pack={editPack} onSave={handleSavePack} onCancel={() => { setShowForm(false); setEditPack(null); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!pricingPack} onOpenChange={v => { if (!v) setPricingPack(null); }}>
        {pricingPack && <PartnerPricingDialog pack={pricingPack} token={token} onClose={() => setPricingPack(null)} onUpdated={() => { fetchPacks(); setPricingPack(null); }} />}
      </Dialog>

      <Dialog open={showTopUp} onOpenChange={setShowTopUp}>
        {showTopUp && <ManualTopUpDialog token={token} onClose={() => setShowTopUp(false)} onSuccess={() => { fetchStats(); }} />}
      </Dialog>
    </div>
  );
}
